import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEstimationStore } from '../../stores/estimationStore';
import { computeN1, FALLBACK_RATIOS } from '../../utils/estimation';
import { InfoTip } from '../shared/InfoTip';
import { fmtCHF, fmt, fmtPct } from '../../utils/format';
import styles from './StressTestPage.module.css';

interface StressParam {
  key: string;
  labelKey: string;
  min: number;
  max: number;
  step: number;
  value: number;
  unit: string;
  icon: string;
}

interface Preset {
  key: string;
  params: Record<string, number>;
}

const PRESETS: Preset[] = [
  { key: 'rate', params: { interestDelta: 2, costMultiplier: 1, delayYears: 0, popGrowthDelta: 0, densityShock: 0 } },
  { key: 'costs', params: { interestDelta: 0, costMultiplier: 0.2, delayYears: 0, popGrowthDelta: 0, densityShock: 0 } },
  { key: 'delay', params: { interestDelta: 0, costMultiplier: 0, delayYears: 3, popGrowthDelta: 0, densityShock: 0 } },
  { key: 'combined', params: { interestDelta: 1.5, costMultiplier: 0.15, delayYears: 2, popGrowthDelta: -0.5, densityShock: -10 } },
];

function computeStressedValues(
  ha: number,
  haZi: number,
  tenantRatios: Record<string, number>,
  tenantBenchmarks: Record<string, number>,
  params: { interestDelta: number; costMultiplier: number; delayYears: number; popGrowthDelta: number; densityShock: number },
) {
  // Base estimation
  const base = computeN1(ha, haZi, tenantRatios, tenantBenchmarks);

  // Apply density shock to logements_per_ha
  const stressedRatios = { ...tenantRatios };
  const baseDensity = stressedRatios['logements_per_ha'] ?? FALLBACK_RATIOS['logements_per_ha'] ?? 65;
  stressedRatios['logements_per_ha'] = baseDensity * (1 + params.densityShock / 100);

  // Apply population growth deviation to inhabitants_per_logement
  const baseInhab = stressedRatios['inhabitants_per_logement'] ?? FALLBACK_RATIOS['inhabitants_per_logement'] ?? 2.3;
  stressedRatios['inhabitants_per_logement'] = baseInhab * (1 + params.popGrowthDelta / 100);

  const stressed = computeN1(ha, haZi, stressedRatios, tenantBenchmarks);

  // Apply cost multiplier
  const costFactor = 1 + params.costMultiplier;
  stressed.cost_total = Math.round(stressed.cost_total * costFactor);
  stressed.cost_total_mix = Math.round(stressed.cost_total_mix * costFactor);
  stressed.cost_infra_mix = Math.round(stressed.cost_infra_mix * costFactor);
  stressed.cost_ecoles = Math.round(stressed.cost_ecoles * costFactor);
  stressed.cost_creches = Math.round(stressed.cost_creches * costFactor);
  stressed.cost_ep_mix = Math.round(stressed.cost_ep_mix * costFactor);
  stressed.cost_co = Math.round(stressed.cost_co * costFactor);
  stressed.cost_sport = Math.round(stressed.cost_sport * costFactor);

  // Financing cost: interest on total cost over construction + delay period
  const constructionYears = 5;
  const totalYears = constructionYears + params.delayYears;
  const baseInterest = 0.02; // 2% baseline
  const stressedInterest = baseInterest + params.interestDelta / 100;
  const baseFinancingCost = Math.round(base.cost_total * baseInterest * constructionYears);
  const stressedFinancingCost = Math.round(stressed.cost_total * stressedInterest * totalYears);

  return {
    base,
    stressed,
    baseFinancingCost,
    stressedFinancingCost,
    totalCostWithFinancing: stressed.cost_total + stressedFinancingCost,
    baseTotalCostWithFinancing: base.cost_total + baseFinancingCost,
    delayYears: params.delayYears,
  };
}

export function StressTestPage() {
  const { t } = useTranslation(['stress', 'common']);
  const ha = useEstimationStore((s) => s.ha);
  const haZi = useEstimationStore((s) => s.haZi);
  const tenantRatios = useEstimationStore((s) => s.tenantRatios);
  const tenantBenchmarks = useEstimationStore((s) => s.tenantBenchmarks);

  const [interestDelta, setInterestDelta] = useState(0);
  const [costMultiplier, setCostMultiplier] = useState(0);
  const [delayYears, setDelayYears] = useState(0);
  const [popGrowthDelta, setPopGrowthDelta] = useState(0);
  const [densityShock, setDensityShock] = useState(0);
  const [activePreset, setActivePreset] = useState<string | null>(null);

  const stressParams: StressParam[] = [
    { key: 'interestDelta', labelKey: 'interestRate', min: -2, max: 5, step: 0.25, value: interestDelta, unit: 'pp', icon: '\u{1F4C8}' },
    { key: 'costMultiplier', labelKey: 'costChange', min: -0.3, max: 0.5, step: 0.05, value: costMultiplier, unit: '%', icon: '\u{1F3D7}' },
    { key: 'delayYears', labelKey: 'projectDelay', min: 0, max: 5, step: 1, value: delayYears, unit: 'yr', icon: '\u23F1' },
    { key: 'popGrowthDelta', labelKey: 'popGrowthDelta', min: -5, max: 5, step: 0.5, value: popGrowthDelta, unit: '%', icon: '\u{1F465}' },
    { key: 'densityShock', labelKey: 'densityShock', min: -20, max: 20, step: 1, value: densityShock, unit: '%', icon: '\u{1F3D8}' },
  ];

  const setters: Record<string, (v: number) => void> = {
    interestDelta: setInterestDelta,
    costMultiplier: setCostMultiplier,
    delayYears: setDelayYears,
    popGrowthDelta: setPopGrowthDelta,
    densityShock: setDensityShock,
  };

  const applyPreset = useCallback((preset: Preset) => {
    setInterestDelta(preset.params.interestDelta ?? 0);
    setCostMultiplier(preset.params.costMultiplier ?? 0);
    setDelayYears(preset.params.delayYears ?? 0);
    setPopGrowthDelta(preset.params.popGrowthDelta ?? 0);
    setDensityShock(preset.params.densityShock ?? 0);
    setActivePreset(preset.key);
  }, []);

  const resetAll = useCallback(() => {
    setInterestDelta(0);
    setCostMultiplier(0);
    setDelayYears(0);
    setPopGrowthDelta(0);
    setDensityShock(0);
    setActivePreset(null);
  }, []);

  const results = useMemo(
    () => computeStressedValues(ha, haZi, tenantRatios, tenantBenchmarks, {
      interestDelta, costMultiplier, delayYears, popGrowthDelta, densityShock,
    }),
    [ha, haZi, tenantRatios, tenantBenchmarks, interestDelta, costMultiplier, delayYears, popGrowthDelta, densityShock],
  );

  const { base, stressed } = results;

  // Impact metrics
  const impacts = [
    { label: t('stress:totalCost'), base: base.cost_total, stressed: stressed.cost_total, format: fmtCHF },
    { label: t('stress:financingCost'), base: results.baseFinancingCost, stressed: results.stressedFinancingCost, format: fmtCHF },
    { label: t('stress:totalWithFinancing'), base: results.baseTotalCostWithFinancing, stressed: results.totalCostWithFinancing, format: fmtCHF },
    { label: t('stress:logements'), base: base.logements, stressed: stressed.logements, format: (n: number) => fmt(n) },
    { label: t('stress:inhabitants'), base: base.inhabitants, stressed: stressed.inhabitants, format: (n: number) => fmt(n) },
    { label: t('stress:emplois'), base: base.emplois, stressed: stressed.emplois, format: (n: number) => fmt(n) },
    { label: t('stress:sbpTotal'), base: base.sbp_total, stressed: stressed.sbp_total, format: (n: number) => `${fmt(n)} m\u00B2` },
    { label: t('stress:costPerLogement'), base: base.logements ? base.cost_total / base.logements : 0, stressed: stressed.logements ? stressed.cost_total / stressed.logements : 0, format: (n: number) => `CHF ${fmt(Math.round(n))}` },
  ];

  // Waterfall breakdown
  const waterfallItems = [
    { label: t('stress:infrastructure'), base: base.cost_infra_mix, stressed: stressed.cost_infra_mix, color: '#22d3ee' },
    { label: t('stress:schools'), base: base.cost_ecoles, stressed: stressed.cost_ecoles, color: '#6366f1' },
    { label: t('stress:childcare'), base: base.cost_creches, stressed: stressed.cost_creches, color: '#f39c12' },
    { label: t('stress:publicSpaces'), base: base.cost_ep_mix, stressed: stressed.cost_ep_mix, color: '#9b59b6' },
    { label: t('stress:communityCtr'), base: base.cost_co, stressed: stressed.cost_co, color: '#e74c3c' },
    { label: t('stress:sports'), base: base.cost_sport, stressed: stressed.cost_sport, color: '#2ecc71' },
    { label: t('stress:financingCost'), base: results.baseFinancingCost, stressed: results.stressedFinancingCost, color: '#e67e22' },
  ];

  const maxWaterfall = Math.max(...waterfallItems.map((w) => Math.max(w.base, w.stressed)), 1);

  function formatSliderValue(param: StressParam): string {
    if (param.key === 'costMultiplier') return `${param.value >= 0 ? '+' : ''}${(param.value * 100).toFixed(0)}%`;
    if (param.unit === 'pp') return `${param.value >= 0 ? '+' : ''}${param.value.toFixed(2)} pp`;
    if (param.unit === '%') return `${param.value >= 0 ? '+' : ''}${param.value.toFixed(1)}%`;
    if (param.unit === 'yr') return `+${param.value} ${t('stress:years')}`;
    return String(param.value);
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2>{t('stress:title')}</h2>
          <div className={styles.headerSub}>
            {t('stress:subtitle', { haTotal: ha, haZI: haZi })}
          </div>
        </div>
        <button className={styles.presetBtn} onClick={resetAll}>{t('stress:resetAll')}</button>
      </div>

      {/* Preset scenarios */}
      <div className={styles.presetRow}>
        {PRESETS.map((p) => (
          <button
            key={p.key}
            className={`${styles.presetBtn} ${activePreset === p.key ? styles.presetBtnActive : ''}`}
            onClick={() => applyPreset(p)}
          >
            {t(`stress:preset${p.key.charAt(0).toUpperCase() + p.key.slice(1)}`)}
          </button>
        ))}
      </div>

      {/* Stress parameter controls */}
      <div className={styles.controls}>
        {stressParams.map((param) => (
          <div key={param.key} className={styles.controlCard}>
            <div className={styles.controlTitle}>
              <span className={styles.controlIcon}>{param.icon}</span>
              {t(`stress:${param.labelKey}`)}<InfoTip text={t(`stress:tooltips.${param.labelKey}`)} />
            </div>
            <div className={styles.sliderRow}>
              <span className={styles.sliderLabel}>
                {param.key === 'costMultiplier' ? `${(param.min * 100).toFixed(0)}%` : `${param.min}${param.unit}`}
              </span>
              <input
                type="range"
                className={styles.slider}
                min={param.min}
                max={param.max}
                step={param.step}
                value={param.value}
                onChange={(e) => {
                  setters[param.key]?.(Number(e.target.value));
                  setActivePreset(null);
                }}
              />
              <span className={styles.sliderValue}>{formatSliderValue(param)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Impact summary cards */}
      <div className={styles.impactRow}>
        {impacts.map((imp) => {
          const delta = imp.stressed - imp.base;
          const deltaPct = imp.base !== 0 ? (delta / Math.abs(imp.base)) * 100 : 0;
          const isUp = delta > 0;
          return (
            <div key={imp.label} className={styles.impactCard}>
              <div className={styles.impactLabel}>{imp.label}</div>
              <div className={styles.impactBase}>{t('stress:baseCase')}: {imp.format(imp.base)}</div>
              <div className={styles.impactStressed}>{imp.format(imp.stressed)}</div>
              {delta !== 0 && (
                <div className={`${styles.impactDelta} ${isUp ? styles.deltaUp : styles.deltaDown}`}>
                  {isUp ? '\u25B2' : '\u25BC'} {imp.format(Math.abs(delta))} ({deltaPct >= 0 ? '+' : ''}{fmtPct(deltaPct, 1)})
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Waterfall chart */}
      <div className={styles.waterfallSection}>
        <div className={styles.sectionTitle}>{t('stress:costWaterfall')}<InfoTip text={t('stress:tooltips.costWaterfall')} /></div>
        {waterfallItems.map((item) => {
          const basePct = (item.base / maxWaterfall) * 100;
          const stressedPct = (item.stressed / maxWaterfall) * 100;
          const delta = item.stressed - item.base;
          return (
            <div key={item.label} className={styles.waterfallRow}>
              <div className={styles.waterfallLabel}>{item.label}</div>
              <div className={styles.waterfallBarWrap}>
                {/* Base bar (dim) */}
                <div
                  className={styles.waterfallBar}
                  style={{
                    left: 0,
                    width: `${basePct}%`,
                    background: item.color,
                    opacity: 0.25,
                    top: 2,
                    bottom: 2,
                  }}
                />
                {/* Stressed bar (bright, half-height, centered) */}
                <div
                  className={styles.waterfallBar}
                  style={{
                    left: 0,
                    width: `${stressedPct}%`,
                    background: item.color,
                    top: 5,
                    bottom: 5,
                  }}
                />
              </div>
              <div className={styles.waterfallValue}>
                {fmtCHF(item.stressed)}
                {delta !== 0 && (
                  <span style={{ fontSize: 10, marginLeft: 4, color: delta > 0 ? '#e74c3c' : '#6366f1' }}>
                    {delta > 0 ? '+' : ''}{fmtPct(item.base !== 0 ? (delta / item.base) * 100 : 0, 0)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        <div className={styles.waterfallRow} style={{ borderTop: '1px solid rgba(255,255,255,0.1)', fontWeight: 600 }}>
          <div className={styles.waterfallLabel}>{t('stress:totalWithFinancing')}</div>
          <div className={styles.waterfallBarWrap}>
            <div
              className={styles.waterfallBar}
              style={{ left: 0, width: '100%', background: 'rgba(255,255,255,0.06)' }}
            />
          </div>
          <div className={styles.waterfallValue}>{fmtCHF(results.totalCostWithFinancing)}</div>
        </div>
      </div>
    </div>
  );
}
